import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { toast } from "react-toastify";
import { injectIntl, IntlShape } from "react-intl";
import { useParams } from "react-router-dom";
import PageSubheader from "../../../../components/PageSubheader";
import overrideClasses from "../../../../utils/overrideClasses";
import OptionsTable from "../../booking/book/options/options-table";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import { getTranslation } from "../../../../utils/translation";
import IntlMessages from "../../../../utils/messages";
import Block from "../../../../components/Tailwind/Block";
import Button from "../../../../components/Tailwind/Button";
import Tag from "../../../../components/Tag";
import {
  Product,
  ProductOption,
} from "../../../../lib/ground-aws-graphql-core/models/Product";
import { EnumBookingFrom, EnumPaths } from "../../../../utils/navigation";
import { PriceUnit } from "../../../../lib/ground-aws-graphql-core/models/PriceUnit";
import history from "../../../../history";

const MAXIMUM_OPTIONS_QUANTITY = 10;

interface Props {
  location: Location<{
    product: Product;
    participants: number;
    time: { start: Date; end: Date };
    date: Date;
    productId: string;
    from: EnumBookingFrom;
    comment: string;
    selectedPriceUnit: PriceUnit;
    selectedPaymentMethod: string;
    userId: string;
    quoteRequestId: number;
  }>;
  intl: IntlShape;
}

const QuoteOptions = (props: Props) => {
  const {
    location: { state },
    intl,
  } = props;

  const {
    userId,
    product,
    participants,
    time,
    productId,
    selectedPriceUnit,
    quoteRequestId,
  } = state;

  const { cid } = useParams<{ cid: string }>();
  const [loading, setLoading] = useState(false);
  const [optionsSelected, setOptionsSelected] = useState<ProductOption[]>([]);

  const searchProductOptions = GroundGraphqlContextStore.useStoreActions(
    a => a.product.searchProductOptions
  );

  const productOptions = GroundGraphqlContextStore.useStoreState(
    s => s.product.productOptions
  );

  const createQuote = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.createQuote
  );

  useEffect(() => {
    setLoading(true);

    Promise.all([
      searchProductOptions({
        buildingId: cid,
        productId,
        priceUnitVariationId: selectedPriceUnit.variation.id,
      }),
    ]).finally(() => setLoading(false));
  }, []);

  const handleCreateQuote = async () => {
    setLoading(true);

    createQuote({
      products: [
        {
          id: product.id,
          booking: {
            total_participants: participants || 1,
            start_date: time.start.toISOString(),
            end_date: time.end.toISOString(),
          },
          price_unit: { id: selectedPriceUnit.id },
          quantity: 1,
          ...(getOptions(optionsSelected, selectedPriceUnit).length > 0 && {
            options: getOptions(optionsSelected, selectedPriceUnit),
          }),
        },
      ],
      user: {
        id: userId,
      },
      building: {
        id: cid,
      },
      ...(quoteRequestId && {
        quote_request: { id: quoteRequestId },
      }),
    })
      .then(() => {
        history.push(
          `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${cid}/${EnumPaths.QUOTES}`
        );
        toast(
          intl.formatMessage({
            id: "page.quote.create.quote.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(intl.formatMessage({ id: "page.quote.create.quote.error" }), {
          type: "error",
        });
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getOptions = (
    optionsSelected: ProductOption[],
    selectedPriceUnit: PriceUnit
  ) => {
    if (optionsSelected.length > 0) {
      const options = optionsSelected.map(option => {
        const puId = option.price_units?.items.find(
          pu => pu.variation?.id === selectedPriceUnit.variation.id
        )?.id;

        if (!puId) return;

        return {
          id: option.id,
          price_unit: {
            id: puId,
          },
          quantity: 1,
        };
      });

      return options.filter(o => !!o);
    }

    return [];
  };

  const selectOption = (option: ProductOption) => {
    if (optionsSelected.includes(option))
      setOptionsSelected(
        optionsSelected.filter(optionSelected => optionSelected !== option)
      );
    else if (optionsSelected.length === MAXIMUM_OPTIONS_QUANTITY)
      toast(
        intl.formatMessage({
          id: "page.booking.options.max.quantity",
        }),
        {
          type: "error",
        }
      );
    else setOptionsSelected([...optionsSelected, option]);
  };

  const removeOption = (id: string) =>
    setOptionsSelected(
      optionsSelected.filter(optionSelected => optionSelected.id !== id)
    );

  return !productOptions ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses({ hidden: loading })}>
        <Helmet>
          <title>
            {intl.formatMessage({
              id: "page.quote.options.document.title",
            })}
          </title>
        </Helmet>

        <PageSubheader title="page.quote.options.title" />

        <div className="px-8 pt-8">
          <IntlMessages
            id="page.quote.options.price.unit"
            values={{
              priceUnit: getTranslation(selectedPriceUnit?.variation.name),
            }}
          />

          <div className="flex space-x-4">
            <OptionsTable
              productOptions={productOptions.filter(
                po =>
                  po?.price_units?.items?.length &&
                  po?.price_units?.items?.length > 0
              )}
              selectOption={selectOption}
              optionsSelected={optionsSelected}
            />

            <div className="flex flex-col">
              <Block className="p-4 w-72">
                <div className="space-y-2">
                  {optionsSelected.map(option => (
                    <Tag
                      key={option.id}
                      id={option.id}
                      title={getTranslation(option.name)}
                      deleteCondition
                      handleDelete={() => removeOption(option.id)}
                    />
                  ))}
                </div>
              </Block>

              <Button
                id="btn-add-quote-options"
                name="btn-add-quote-options"
                item={null}
                type="submit"
                onClick={handleCreateQuote}
                className="self-end"
              >
                <span className="text-center">
                  <IntlMessages id="general.submit" />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default injectIntl(QuoteOptions);
